<script setup lang="ts">
import type { Route } from '@/composables/useHistory'
import LayoutHeader from '@/components/modules/Layout/LayoutHeader.vue'
import LayoutSidebar from '@/components/modules/Layout/LayoutSidebar.vue'
import CountriesSprite from '@/components/shared/CountriesSprite.vue'
import { useRouteHistory } from '@/composables/useHistory'
import { useProjectedSelf } from '@/composables/useProjectedSelf'
import { infiniteCacheConfig } from '@/config/query.config'

import { getThemeSettings } from '@/lib'
import { getCampaignPolicies } from '@/services/campaign.service'
import { getSchema } from '@/services/schema.service'
import { useQuery } from '@tanstack/vue-query'
import { useWindowSize } from '@vueuse/core'
import { ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { Toaster } from 'vue-sonner'

defineProps<{
  isDark: boolean
}>()

const { sidebarLocation, showLogin } = getThemeSettings()
const { width } = useWindowSize()

const isSidebarRight = sidebarLocation === 'right'

const isSidebarVisible = ref(true)

watch(
  width,
  (newWidth) => {
    isSidebarVisible.value = newWidth > 1024
  },
  { immediate: true },
)

const { addRoute } = useRouteHistory()

const router = useRouter()

router.beforeEach((_, from) => {
  if (from) {
    const route: Route = {
      name: from.name as string,
      path: from.path,
    }
    addRoute(route)
  }
  return true
})
useQuery({
  queryKey: ['campaign-policies'],
  queryFn: getCampaignPolicies,
  ...infiniteCacheConfig,
  enabled: !showLogin,
})

useQuery({
  queryKey: ['schema'],
  queryFn: getSchema,
  ...infiniteCacheConfig,
  enabled: !showLogin,
})

// Prefetch projected self data once on app load
useProjectedSelf()
</script>

<template>
  <!-- Countries Sprite -->
  <div id="countries-sprite" class="hidden">
    <CountriesSprite />
  </div>

  <!-- Global Toaster -->
  <Teleport to="html">
    <Toaster
      position="top-right"
      rich-colors
      :style="{ zIndex: 'var(--z-toast)' }"
      :toast-options="{
        class: 'mt-12',
      }"
    />
  </Teleport>

  <div
    class="flex overflow-hidden absolute inset-0 flex-col print:static print:block print:overflow-visible"
    :class="{ dark: isDark }"
  >
    <LayoutHeader
      class="shrink-0 print:hidden"
      :visible="isSidebarVisible"
      @toggle-sidebar="isSidebarVisible = !isSidebarVisible"
    />
    <main
      id="main-container"
      class="flex flex-1 min-h-0 print:block print:overflow-visible"
      :class="{ 'flex-row-reverse': isSidebarRight }"
    >
      <LayoutSidebar class="print:hidden" :visible="isSidebarVisible" />
      <article
        class="flex overflow-hidden relative flex-col flex-1 min-h-0 bg-gradient-to-b from-gray-100 print:block print:overflow-visible print:bg-none print:bg-white dark:bg-gray-900 dark:from-gray-900"
      >
        <div
          id="main-content"
          class="flex overflow-y-auto flex-col flex-1 min-h-0 print:block print:overflow-visible"
        >
          <slot />
        </div>
        <div
          class="px-3 pt-1 pb-1.5 bg-white border-t border-gray-300 print:hidden dark:border-gray-700 dark:bg-gray-900"
        >
          <span class="text-sm text-gray-900 dark:text-white"
            >© 2019-{{ new Date().getFullYear() }} SoftwareIDM Inc.</span
          >
        </div>
      </article>
    </main>
  </div>
</template>
